import React, { useState } from "react";
import { Plus, Pencil, CheckCircle2, AlertTriangle, ChevronRight, BarChart3, Target, CalendarClock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { fmtDateFr, relAgo, triLabel, triCls, daysUntil } from "./placementsShared";
import { AddFollowUpDialog, CompleteFollowUpDialog } from "./FollowUpDialogs";
import { useLang, translateFr as tfr } from "@/i18n";

function TimelineDot({ state }) {
  if (state === "COMPLETED") return <span className="h-3 w-3 rounded-full bg-success ring-4 ring-success/15 shrink-0" />;
  if (state === "OVERDUE") return <span className="h-3 w-3 rounded-full bg-warning ring-4 ring-warning/15 shrink-0" />;
  if (state === "CANCELLED") return <span className="h-3 w-3 rounded-full bg-muted-foreground/30 shrink-0" />;
  return <span className="h-3 w-3 rounded-full border-2 border-muted-foreground/40 bg-card shrink-0" />;
}

function RailCard({ title, Icon, children, testid, highlight }) {
  return (
    <section className={cn("rounded-2xl border p-4",
      highlight ? "border-info/25 bg-info/[0.04]" : "border-border dark:border-border bg-card")}
      data-testid={testid}>
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
        <h4 className="text-[11px] uppercase tracking-[0.16em] font-semibold text-foreground/85">{title}</h4>
      </div>
      {children}
    </section>
  );
}

export default function SuiviTab({ detail, onRefresh }) {
  const { tf } = useLang();
  const [addOpen, setAddOpen] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [editingObjective, setEditingObjective] = useState(false);
  const [objDraft, setObjDraft] = useState("");

  const followups = detail.followups || [];
  const isActive = detail.status === "ACTIVE";
  const situation = detail.current_situation;
  const nextFu = detail.next_followup;
  const signals = detail.health?.signals || [];

  const saveObjective = async () => {
    try {
      await api.patch(`/placements/${detail.id}`, { objective: objDraft });
      toast.success(tf("Objectif mis à jour"));
      setEditingObjective(false);
      onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };

  const cancelFu = async (fid) => {
    try {
      await api.patch(`/placements/${detail.id}/followups/${fid}`, { status: "CANCELLED" });
      onRefresh?.();
    } catch { toast.error(tf("Erreur")); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.42fr)] gap-4">
      {/* Timeline */}
      <section className="rounded-2xl border border-border dark:border-border bg-card p-4 md:p-5" data-testid="followup-timeline">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold">{tf("Timeline de suivi")}</h3>
          {isActive && (
            <Button size="sm" variant="outline" onClick={() => setAddOpen(true)}
              className="h-8 gap-1.5 border-border dark:border-border" data-testid="add-followup-btn">
              <Plus className="h-3.5 w-3.5" /> {tf("Ajouter un suivi")}
            </Button>
          )}
        </div>
        {followups.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="timeline-empty">
            {tf("Aucun suivi planifié pour ce placement.")}
          </div>
        ) : (
          <ol className="relative space-y-0">
            {followups.map((f, idx) => {
              const st = f.derived_state;
              const late = st === "OVERDUE" ? Math.abs(daysUntil(f.scheduled_at) ?? 0) : 0;
              return (
                <li key={f.id} className="relative flex gap-3.5 pb-5 last:pb-0" data-testid={`timeline-item-${f.id}`}>
                  {idx < followups.length - 1 && (
                    <span className="absolute left-[5px] top-4 bottom-0 w-px bg-border dark:bg-secondary/50" aria-hidden="true" />
                  )}
                  <div className="pt-1"><TimelineDot state={st} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-[11px] text-muted-foreground tabular-nums">{fmtDateFr(f.scheduled_at)}</div>
                        <div className={cn("text-sm font-medium mt-0.5", st === "CANCELLED" && "line-through text-muted-foreground")}>
                          {f.label}
                        </div>
                        {st === "COMPLETED" && (f.notes || triLabel(f.sporting_state)) && (
                          <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                            {f.notes || [triLabel(f.sporting_state) && `Sportif : ${tf(triLabel(f.sporting_state))}`].filter(Boolean).join(" · ")}
                          </div>
                        )}
                        {st === "OVERDUE" && (
                          <div className="text-xs text-warning mt-0.5 inline-flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" /> {tf("En retard de")} {late} {late > 1 ? tf("jours") : tf("jour")}
                          </div>
                        )}
                        {st === "SCHEDULED" && <div className="text-xs text-muted-foreground/70 mt-0.5">{tf("À venir")}</div>}
                      </div>
                      {isActive && (st === "SCHEDULED" || st === "OVERDUE") && (
                        <div className="flex items-center gap-1 shrink-0">
                          <Button size="sm" variant="outline" onClick={() => setCompleting(f)}
                            className="h-7 px-2.5 text-xs border-border dark:border-border" data-testid={`complete-fu-${f.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {tf("Compléter")}
                          </Button>
                          <button onClick={() => cancelFu(f.id)} aria-label={tf("Annuler ce suivi")}
                            className="h-7 px-1.5 rounded text-muted-foreground/60 hover:text-foreground text-xs"
                            data-testid={`cancel-fu-${f.id}`}>✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </section>

      {/* Right rail */}
      <div className="space-y-3 min-w-0">
        <RailCard title={tf("Objectif du placement")} Icon={Target} testid="card-objective">
          {editingObjective ? (
            <div className="space-y-2">
              <Textarea value={objDraft} onChange={(e) => setObjDraft(e.target.value)}
                className="bg-background/60 border-border dark:border-border min-h-[80px] text-sm" data-testid="objective-input" />
              <div className="flex gap-2">
                <Button size="sm" onClick={saveObjective} className="h-8" data-testid="objective-save">{tf("Enregistrer")}</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingObjective(false)} className="h-8">{tf("Annuler")}</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-start justify-between gap-2">
              <p className={cn("text-sm leading-relaxed", !detail.objective && "text-muted-foreground/60")} data-testid="objective-text">
                {detail.objective ? `« ${detail.objective} »` : tf("Non renseigné")}
              </p>
              {isActive && (
                <button onClick={() => { setObjDraft(detail.objective || ""); setEditingObjective(true); }}
                  className="text-muted-foreground/50 hover:text-foreground shrink-0" aria-label={tf("Modifier l'objectif")}
                  data-testid="objective-edit">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </RailCard>

        <RailCard title={tf("Situation actuelle")} Icon={BarChart3} testid="card-situation">
          <div className="space-y-2">
            {[
              ["Situation sportive", situation?.sporting],
              ["Relation club", situation?.club_relationship],
              [tfr("Satisfaction athlète"), situation?.athlete_satisfaction],
            ].map(([label, v]) => (
              <div key={label} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-muted-foreground text-xs">{tf(label)}</span>
                {v ? (
                  <span className={cn("text-[10px] font-medium rounded-full border px-2 py-0.5", triCls(v))}>{tf(triLabel(v))}</span>
                ) : (
                  <span className="text-xs text-muted-foreground/50">{tf("Non renseigné")}</span>
                )}
              </div>
            ))}
          </div>
        </RailCard>

        <RailCard title={tf("Prochaine action")} Icon={CalendarClock} testid="card-next-action" highlight={!!nextFu}>
          {nextFu ? (
            <button onClick={() => {
              const fu = followups.find((f) => f.id === nextFu.id);
              if (fu && isActive) setCompleting(fu);
            }} className="w-full text-left group" data-testid="next-action-btn">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{nextFu.label}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {fmtDateFr(nextFu.scheduled_at)}
                    {nextFu.days != null && nextFu.days < 0 && (
                      <span className="text-warning ml-1.5">{tf("En retard de")} {Math.abs(nextFu.days)} {Math.abs(nextFu.days) > 1 ? tf("jours") : tf("jour")}</span>
                    )}
                    {nextFu.days != null && nextFu.days >= 0 && <span className="ml-1.5">· {relAgo(nextFu.scheduled_at)}</span>}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/50 group-hover:text-foreground transition-colors" />
              </div>
            </button>
          ) : (
            <p className="text-sm text-muted-foreground/60">{tf("Aucune action planifiée.")}</p>
          )}
        </RailCard>

        <RailCard title={tf("Signaux")} Icon={AlertTriangle} testid="card-signals">
          {signals.length === 0 ? (
            <div className="flex items-start gap-2" data-testid="signals-empty">
              <CheckCircle2 className="h-4 w-4 text-success mt-0.5 shrink-0" />
              <div>
                <div className="text-sm font-medium text-success">{tf("Aucun signal d'alerte")}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{tf("Le suivi est à jour.")}</div>
              </div>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="signals-list">
              {signals.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <AlertTriangle className={cn("h-3.5 w-3.5 mt-0.5 shrink-0",
                    s.severity === "high" ? "text-athlynx" : s.severity === "medium" ? "text-warning" : "text-muted-foreground")} />
                  <span className="text-foreground/90">{s.message}</span>
                </li>
              ))}
            </ul>
          )}
        </RailCard>
      </div>

      <AddFollowUpDialog open={addOpen} onOpenChange={setAddOpen} placementId={detail.id} onSaved={onRefresh} />
      <CompleteFollowUpDialog open={!!completing} onOpenChange={(o) => !o && setCompleting(null)}
        placementId={detail.id} followup={completing} onSaved={() => { setCompleting(null); onRefresh?.(); }} />
    </div>
  );
}
